import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { Check, Pencil, X } from 'lucide-react'
import { Button, Input } from '@atrvd/saas-ui'
import { MANUAL_PAGE_TRANSACTION_SOURCES } from '@/types/banking'
import { useUpdateTransactionNotes } from '@/hooks/useBanking'
import type { BankTxn, BankTxnGroup } from './ViewBankTransactionsDialog'
import { renderOrDash } from './viewBankTransactionsRows'

/** Notes editing state of one transaction. */
interface RowNote {
  /** Present = the row is in edit mode; the value is its draft. */
  draft?: string
  saving: boolean
  /** Raw save error (ADR 0006). */
  error?: string
  /**
   * Note saved in this session, shown until the refetched `groups` carry it —
   * otherwise the row flashes the old note between save and refetch.
   */
  committed?: string
}

/**
 * Keyed by transaction id, never by index or page, so a draft on a row paged
 * away from is still there on paging back.
 */
type NotesState = Record<string, RowNote>

const IDLE: RowNote = { saving: false }

/** Applies `change` to one row; a row back to idle leaves the map. */
function patchRow(state: NotesState, id: string, change: Partial<RowNote>): NotesState {
  const row = { ...(state[id] ?? IDLE), ...change }
  const next = { ...state }
  if (
    row.draft === undefined &&
    !row.saving &&
    row.error === undefined &&
    row.committed === undefined
  ) {
    delete next[id]
  } else {
    next[id] = row
  }
  return next
}

interface NotesEditing {
  notes: NotesState
  begin: (id: string, current: string) => void
  change: (id: string, value: string) => void
  discard: (id: string) => void
  save: (id: string, notes: string) => Promise<void>
}

const NotesEditingContext = createContext<NotesEditing | null>(null)

/**
 * Owns the Notes editing state of the drill-down and persists saves through
 * {@link useUpdateTransactionNotes}. Wrap it around the Modal, not inside it:
 * the Modal unmounts its body while closed, and the state should live as long
 * as the dialog does.
 */
export function TransactionNotesProvider({
  groups,
  children,
}: {
  groups: BankTxnGroup[]
  children: ReactNode
}) {
  const { mutateAsync } = useUpdateTransactionNotes()
  const [notes, setNotes] = useState<NotesState>({})

  // Drop a committed override once the refetched feed carries it, so it only
  // bridges the save→refetch gap and never masks a later external change.
  // Walks every group, not the visible page: a row saved and then paged away
  // from must be pruned too.
  useEffect(() => {
    setNotes((prev) => {
      let next = prev
      for (const group of groups) {
        for (const txn of group.transactions) {
          if (next[txn.id]?.committed === txn.notes) {
            next = patchRow(next, txn.id, { committed: undefined })
          }
        }
      }
      return next
    })
  }, [groups])

  const begin = useCallback((id: string, current: string) => {
    setNotes((prev) => patchRow(prev, id, { draft: current, error: undefined }))
  }, [])

  const change = useCallback((id: string, value: string) => {
    setNotes((prev) => patchRow(prev, id, { draft: value }))
  }, [])

  const discard = useCallback((id: string) => {
    setNotes((prev) => patchRow(prev, id, { draft: undefined, error: undefined }))
  }, [])

  const save = useCallback(
    async (id: string, text: string) => {
      setNotes((prev) => patchRow(prev, id, { saving: true }))
      try {
        await mutateAsync({ id, notes: text })
        setNotes((prev) =>
          patchRow(prev, id, { saving: false, committed: text, draft: undefined, error: undefined })
        )
      } catch (error) {
        // The row stays in edit mode with the message, to retry or discard.
        const message = error instanceof Error ? error.message : 'Failed to save note'
        setNotes((prev) => patchRow(prev, id, { saving: false, error: message }))
      }
    },
    [mutateAsync]
  )

  const value = useMemo(
    () => ({ notes, begin, change, discard, save }),
    [notes, begin, change, discard, save]
  )
  return <NotesEditingContext.Provider value={value}>{children}</NotesEditingContext.Provider>
}

function useNotesEditing(): NotesEditing {
  const editing = useContext(NotesEditingContext)
  if (!editing)
    throw new Error('TransactionNotesCell must be rendered inside TransactionNotesProvider')
  return editing
}

/**
 * Notes cell of one transaction. Editable only for the manual-page sources —
 * the exact set `updateNotes` writes to (a write on any other source matches
 * no row). Every role may edit notes, so there is no permission gate.
 *
 * In the input, Enter saves and Escape discards; Escape stops there so the
 * Modal does not close and drop the other rows' drafts.
 */
export function TransactionNotesCell({ txn }: { txn: BankTxn }) {
  const { notes, begin, change, discard, save } = useNotesEditing()
  // Set when edit mode ends from inside the row: the input and buttons unmount,
  // and focus would fall to <body> — out of the Modal's focus trap, where the
  // next Escape closes the Modal. The pencil takes it back when it mounts.
  const refocusPencil = useRef(false)
  // Set by the pencil, so only an editor the user just opened takes focus —
  // not the drafts that remount when the user pages back to them.
  const focusInput = useRef(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const row = notes[txn.id] ?? IDLE
  const shown = row.committed ?? txn.notes

  // `disabled` while saving drops focus to <body>, outside the Modal's focus
  // trap. On success the pencil takes it back when it mounts; on failure the
  // editor stays, so the input must, or the next Escape closes the Modal.
  useEffect(() => {
    if (row.error !== undefined && !row.saving) inputRef.current?.focus()
  }, [row.error, row.saving])

  if (!(MANUAL_PAGE_TRANSACTION_SOURCES as readonly string[]).includes(txn.source)) {
    return <>{renderOrDash(shown)}</>
  }

  // Per-row names keep the controls distinct while several rows are in edit mode.
  const rowLabel = `${txn.date} — ${txn.description}`
  const { draft } = row

  if (draft === undefined) {
    // min-h-8 = the edit-mode input height, so toggling does not resize the row.
    return (
      <div className="flex min-h-8 items-center gap-1">
        <span>{renderOrDash(shown)}</span>
        <Button
          ref={(button) => {
            if (button && refocusPencil.current) {
              refocusPencil.current = false
              button.focus()
            }
          }}
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => {
            focusInput.current = true
            begin(txn.id, shown)
          }}
          aria-label={`Edit notes for ${rowLabel}`}
          className="h-7 w-7 shrink-0"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
    )
  }

  const saveDraft = () => {
    refocusPencil.current = true
    void save(txn.id, draft)
  }
  const discardDraft = () => {
    refocusPencil.current = true
    discard(txn.id)
  }
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveDraft()
    } else if (event.key === 'Escape') {
      // The Modal closes on a document-level Escape listener.
      event.stopPropagation()
      discardDraft()
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <Input
          ref={(input) => {
            inputRef.current = input
            if (input && focusInput.current) {
              focusInput.current = false
              input.focus()
              // Caret after the note, ready to append.
              input.setSelectionRange(input.value.length, input.value.length)
            }
          }}
          value={draft}
          onChange={(e) => change(txn.id, e.target.value)}
          onKeyDown={onKeyDown}
          disabled={row.saving}
          aria-label={`Edit notes for ${rowLabel}`}
          // min-w-0 lets the input shrink inside the fixed-width column.
          className="h-8 min-w-0"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={saveDraft}
          disabled={row.saving}
          aria-label={`Save notes for ${rowLabel}`}
          className="h-7 w-7 shrink-0"
        >
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={discardDraft}
          disabled={row.saving}
          aria-label={`Discard notes for ${rowLabel}`}
          className="h-7 w-7 shrink-0"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
      {row.error && (
        <p className="text-xs text-destructive" role="alert">
          {row.error}
        </p>
      )}
    </div>
  )
}
